import {
  pagedListWrapper,
  singleItemWrapper
} from '../../../api-client/wrappers'
import type { Starter } from '../../types/internal'
import type {
  Tournament,
  Stage,
  StageUnlock,
  ArchiveTournamentItem,
  PredictionMutationResponse,
  SwissPredictionUpsertBody,
  SwissPredictionRemoveBody,
  BracketPredictionUpsertBody,
  BracketPredictionRemoveBody,
  LeaderboardPage,
  LeaderboardMe,
  RewardsResponse,
  RewardLevel,
  ClaimResponse
} from './types'

// SDK starter для модуля Pickem 1.0.0.
// Контракты: docs/pickem/docs/technical/03-api-basic.md.
//
// Сознательно используем singleItemWrapper только там, где у нас полностью CRUD-семантика.
// Большинство pickem-ручек не вписываются в стандартные обёртки (path-параметры внутри URL,
// тело без id, кастомный shape ответа), поэтому работаем с apiClient напрямую — это ровно
// тот же паттерн, что и в starters/battlepass/index.ts (claimReward).

const pickemPath = (version: number, suffix: string) =>
  `/api/v${version}/pickem${suffix}`

export type PickemModuleApi = {
  getActiveTournament: () => Promise<Tournament | null>
  getLastFinishedTournament: () => Promise<Tournament | null>
  getTournamentById: (id: Tournament['id']) => Promise<Tournament>
  tournamentsArchive: ReturnType<
    ReturnType<typeof pagedListWrapper<'id', ArchiveTournamentItem>>
  >
  getStageById: (id: Stage['id']) => Promise<Stage>
  getStageUnlockConditions: (id: Stage['id']) => Promise<StageUnlock>
  upsertSwissPrediction: (
    body: SwissPredictionUpsertBody,
    idempotencyKey: string
  ) => Promise<PredictionMutationResponse>
  removeSwissPrediction: (
    body: SwissPredictionRemoveBody,
    idempotencyKey: string
  ) => Promise<PredictionMutationResponse>
  upsertBracketPrediction: (
    body: BracketPredictionUpsertBody,
    idempotencyKey: string
  ) => Promise<PredictionMutationResponse>
  removeBracketPrediction: (
    body: BracketPredictionRemoveBody,
    idempotencyKey: string
  ) => Promise<PredictionMutationResponse>
  getLeaderboardPage: (
    tournamentId: Tournament['id'],
    query?: { page?: number; 'per-page'?: number; search?: string },
    options?: { signal?: AbortSignal }
  ) => Promise<LeaderboardPage>
  getLeaderboardMe: (
    tournamentId: Tournament['id'],
    options?: { signal?: AbortSignal }
  ) => Promise<LeaderboardMe>
  getRewards: (tournamentId: Tournament['id']) => Promise<RewardsResponse>
  claimReward: (
    levelId: RewardLevel['id'],
    idempotencyKey: string
  ) => Promise<ClaimResponse>
}

export default {
  factory: ({ apiClient, version }): PickemModuleApi => {
    const { get: getActiveTournament } = singleItemWrapper<Tournament | null>(
      apiClient,
      { path: pickemPath(version, '/tournaments/active') }
    )

    const { get: getLastFinishedTournament } =
      singleItemWrapper<Tournament | null>(apiClient, {
        path: pickemPath(version, '/tournaments/last-finished')
      })

    const tournamentsArchive = pagedListWrapper<'id', ArchiveTournamentItem>()(
      apiClient,
      {
        path: pickemPath(version, '/tournaments/archive'),
        idKey: 'id'
      }
    )

    return {
      getActiveTournament: () => getActiveTournament(),
      getLastFinishedTournament: () => getLastFinishedTournament(),
      tournamentsArchive,

      getTournamentById: (id: Tournament['id']) =>
        apiClient.get<Tournament>(pickemPath(version, `/tournaments/${id}`)),

      getStageById: (id: Stage['id']) =>
        apiClient.get<Stage>(pickemPath(version, `/stages/${id}`)),

      getStageUnlockConditions: (id: Stage['id']) =>
        apiClient.get<StageUnlock>(
          pickemPath(version, `/stages/${id}/unlock-conditions`)
        ),

      upsertSwissPrediction: (
        body: SwissPredictionUpsertBody,
        idempotencyKey: string
      ) =>
        apiClient.post<PredictionMutationResponse>(
          pickemPath(version, '/predictions/swiss'),
          {
            body,
            headers: { 'X-Idempotency-Key': idempotencyKey }
          }
        ),

      removeSwissPrediction: (
        body: SwissPredictionRemoveBody,
        idempotencyKey: string
      ) =>
        apiClient.delete<PredictionMutationResponse>(
          pickemPath(version, '/predictions/swiss'),
          {
            body,
            headers: { 'X-Idempotency-Key': idempotencyKey }
          }
        ),

      upsertBracketPrediction: (
        body: BracketPredictionUpsertBody,
        idempotencyKey: string
      ) =>
        apiClient.post<PredictionMutationResponse>(
          pickemPath(version, '/predictions/bracket'),
          {
            body,
            headers: { 'X-Idempotency-Key': idempotencyKey }
          }
        ),

      removeBracketPrediction: (
        body: BracketPredictionRemoveBody,
        idempotencyKey: string
      ) =>
        apiClient.delete<PredictionMutationResponse>(
          pickemPath(version, '/predictions/bracket'),
          {
            body,
            headers: { 'X-Idempotency-Key': idempotencyKey }
          }
        ),

      getLeaderboardPage: (
        tournamentId: Tournament['id'],
        query: { page?: number; 'per-page'?: number; search?: string } = {},
        options: { signal?: AbortSignal } = {}
      ) =>
        apiClient.get<LeaderboardPage>(
          pickemPath(version, `/tournaments/${tournamentId}/leaderboard`),
          { query, signal: options.signal }
        ),

      getLeaderboardMe: (
        tournamentId: Tournament['id'],
        options: { signal?: AbortSignal } = {}
      ) =>
        apiClient.get<LeaderboardMe>(
          pickemPath(version, `/tournaments/${tournamentId}/leaderboard/me`),
          { signal: options.signal }
        ),

      getRewards: (tournamentId: Tournament['id']) =>
        apiClient.get<RewardsResponse>(
          pickemPath(version, `/tournaments/${tournamentId}/rewards`)
        ),

      claimReward: (levelId: RewardLevel['id'], idempotencyKey: string) =>
        apiClient.post<ClaimResponse>(
          pickemPath(version, `/rewards/${levelId}/claim`),
          {
            headers: { 'X-Idempotency-Key': idempotencyKey }
          }
        )
    }
  }
} satisfies Starter
